import { AlertTriangle, ExternalLink, Info, ShieldAlert } from "lucide-react";
import type { ManualBlock, ManualImage } from "@/content/manualTypes";
import { cn } from "@/lib/utils";

const noteConfig = {
  note: { icon: Info, label: "Note", className: "border-border bg-card/60 text-foreground" },
  important: {
    icon: ShieldAlert,
    label: "Important",
    className: "border-primary/40 bg-primary/10 text-foreground",
  },
  warning: {
    icon: AlertTriangle,
    label: "Warning",
    className: "border-destructive/40 bg-destructive/10 text-foreground",
  },
} as const;

const ManualFigure = ({ image }: { image: ManualImage }) => (
  <figure className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card/50">
    <div
      className={cn(
        "flex flex-1 items-center justify-center p-4 sm:p-6",
        image.light ? "bg-white" : "bg-background/60",
      )}
    >
      <img
        src={image.src}
        alt={image.alt}
        loading="lazy"
        decoding="async"
        className="max-h-[420px] w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
      />
    </div>
    {image.caption && (
      <figcaption className="border-t border-border px-4 py-3 text-sm leading-relaxed text-muted-foreground">
        {image.caption}
      </figcaption>
    )}
  </figure>
);

export const ManualBlockRenderer = ({ block }: { block: ManualBlock }) => {
  switch (block.type) {
    case "lead":
      return <p className="text-lg leading-relaxed text-foreground/90 sm:text-xl">{block.text}</p>;

    case "paragraph":
      return <p className="max-w-[70ch] leading-relaxed text-foreground/80">{block.text}</p>;

    case "heading":
      return (
        <h3 className="pt-2 text-lg font-semibold tracking-tight text-foreground sm:text-xl">{block.text}</h3>
      );

    case "list":
      return (
        <ul className="max-w-[70ch] space-y-2.5">
          {block.items.map((item) => (
            <li key={item} className="flex gap-3 leading-relaxed text-foreground/80">
              <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case "steps":
      return (
        <ol className="max-w-[70ch] space-y-3">
          {block.items.map((item, index) => (
            <li key={item} className="flex gap-4">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <span className="leading-relaxed text-foreground/80">{item}</span>
            </li>
          ))}
        </ol>
      );

    case "note": {
      const { icon: Icon, label, className } = noteConfig[block.variant];
      return (
        <div className={cn("flex gap-3 rounded-2xl border p-4 sm:p-5", className)}>
          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <p className="leading-relaxed">
            <span className="font-semibold">{label}. </span>
            <span className="text-foreground/80">{block.text}</span>
          </p>
        </div>
      );
    }

    case "gallery": {
      const columns = block.columns ?? 2;
      return (
        <div
          className={cn(
            "grid gap-4",
            columns === 1 && "grid-cols-1",
            columns === 2 && "grid-cols-1 sm:grid-cols-2",
            columns === 3 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
          )}
        >
          {block.images.map((image) => (
            <ManualFigure key={image.src} image={image} />
          ))}
        </div>
      );
    }

    case "specs":
      return (
        <dl className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
          {block.items.map((item) => (
            <div key={item.label} className="bg-card/60 p-4">
              <dt className="text-sm font-semibold text-primary">{item.label}</dt>
              <dd className="mt-1 text-sm leading-relaxed text-foreground/80">{item.value}</dd>
            </div>
          ))}
        </dl>
      );

    case "table":
      return (
        <div className="-mx-4 overflow-x-auto rounded-2xl border border-border sm:mx-0">
          <table className="w-full min-w-[480px] text-left text-sm">
            <thead>
              <tr className="bg-primary/15">
                {block.head.map((cell) => (
                  <th key={cell} className="px-4 py-3 font-semibold text-foreground">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row.join("-")} className="border-t border-border">
                  {row.map((cell) => (
                    <td key={cell} className="px-4 py-3 text-foreground/80">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "link":
      return (
        <a
          href={block.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {block.label}
          <ExternalLink className="h-4 w-4" aria-hidden />
        </a>
      );

    default:
      return null;
  }
};
